import { useEffect, useState } from 'react';
import { 
  useGetExpiredPostsCountQuery, 
  useGetDeletedPostsCountQuery,
  useGetOrphanedCommentsCountQuery
} from '../redux/adminApiSlice';

interface PostCounts {
  expiredCount: number;
  deletedCount: number;
  orphanedCommentsCount: number;
  isLoading: boolean;
  refetchCounts: () => Promise<void>;
}

export const usePostCounts = (refreshInterval: number = 60000): PostCounts => {
  // Get the queries with skip initially to control when they fire
  const { 
    data: expiredData,
    refetch: refetchExpired,
    isLoading: isLoadingExpired
  } = useGetExpiredPostsCountQuery(undefined, {
    pollingInterval: refreshInterval
  });

  const {
    data: deletedData,
    refetch: refetchDeleted,
    isLoading: isLoadingDeleted
  } = useGetDeletedPostsCountQuery(undefined, {
    pollingInterval: refreshInterval
  });

  const {
    data: orphanedCommentsData,
    refetch: refetchOrphanedComments,
    isLoading: isLoadingOrphanedComments
  } = useGetOrphanedCommentsCountQuery(undefined, {
    pollingInterval: refreshInterval
  });

  const [counts, setCounts] = useState({
    expiredCount: 0,
    deletedCount: 0,
    orphanedCommentsCount: 0
  });

  // Update state when data changes
  useEffect(() => {
    if (expiredData) {
      setCounts(prev => ({ ...prev, expiredCount: expiredData.count }));
    }
  }, [expiredData]);

  useEffect(() => {
    if (deletedData) {
      setCounts(prev => ({ ...prev, deletedCount: deletedData.count }));
    }
  }, [deletedData]);

  useEffect(() => {
    if (orphanedCommentsData) {
      setCounts(prev => ({ ...prev, orphanedCommentsCount: orphanedCommentsData.count }));
    }
  }, [orphanedCommentsData]);

  // Manual refetch function that forces both queries to run
  const refetchCounts = async () => {
    console.log('[Posts] Manual refetch of post counts');
    try {
      await Promise.all([
        refetchExpired(),
        refetchDeleted(),
        refetchOrphanedComments()
      ]);
    } catch (error) {
      console.error('[Posts] Error refetching counts:', error);
    }
  };

  return {
    expiredCount: counts.expiredCount,
    deletedCount: counts.deletedCount,
    orphanedCommentsCount: counts.orphanedCommentsCount,
    isLoading: isLoadingExpired || isLoadingDeleted || isLoadingOrphanedComments,
    refetchCounts
  };
}; 